/**
 * Compute approximate sunrise / sunset for a given lat/lng. Adapted from the
 * NOAA solar algorithm. Returns Date objects in the local timezone of the
 * machine running this. Good to within ~1 minute for civil twilight needs.
 *
 * No external library — keeps bundle lean.
 */

export interface SunTimes {
  sunrise: Date;
  sunset: Date;
}

const MUMBAI = { lat: 19.076, lng: 72.8777 };

export function getSunTimes(date: Date = new Date(), lat = MUMBAI.lat, lng = MUMBAI.lng): SunTimes {
  // Day of year
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const diff = date.getTime() - start;
  const dayOfYear = Math.floor(diff / 86400000);

  const zenith = 90.833; // official sunrise/sunset

  const calc = (rising: boolean): Date => {
    const lngHour = lng / 15;
    const t = rising
      ? dayOfYear + (6 - lngHour) / 24
      : dayOfYear + (18 - lngHour) / 24;
    const M = 0.9856 * t - 3.289;
    let L = M + 1.916 * sinDeg(M) + 0.02 * sinDeg(2 * M) + 282.634;
    L = norm(L, 360);
    let RA = atan2Deg(0.91764 * tanDeg(L)) ;
    RA = norm(RA, 360);
    // Place RA in the same quadrant as L
    const Lquad = Math.floor(L / 90) * 90;
    const RAquad = Math.floor(RA / 90) * 90;
    RA = RA + (Lquad - RAquad);
    RA = RA / 15;
    const sinDec = 0.39782 * sinDeg(L);
    const cosDec = cosDeg(asinDeg(sinDec));
    const cosH = (cosDeg(zenith) - sinDec * sinDeg(lat)) / (cosDec * cosDeg(lat));
    if (cosH > 1) return new Date(NaN); // never rises
    if (cosH < -1) return new Date(NaN); // never sets
    let H = rising ? 360 - acosDeg(cosH) : acosDeg(cosH);
    H = H / 15;
    const T = H + RA - 0.06571 * t - 6.622;
    let UT = T - lngHour;
    UT = norm(UT, 24);
    const hours = Math.floor(UT);
    const mins = Math.floor((UT - hours) * 60);
    const secs = Math.floor((((UT - hours) * 60) - mins) * 60);
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), hours, mins, secs));
  };

  return { sunrise: calc(true), sunset: calc(false) };
}

function norm(v: number, max: number): number {
  let x = v % max;
  if (x < 0) x += max;
  return x;
}
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;
const sinDeg = (d: number) => Math.sin(toRad(d));
const cosDeg = (d: number) => Math.cos(toRad(d));
const tanDeg = (d: number) => Math.tan(toRad(d));
const asinDeg = (x: number) => toDeg(Math.asin(x));
const acosDeg = (x: number) => toDeg(Math.acos(x));
const atan2Deg = (y: number) => toDeg(Math.atan(y));

/** Is it currently after sunset OR before sunrise at the given location? */
export function isNight(now: Date = new Date(), lat?: number, lng?: number): boolean {
  const { sunrise, sunset } = getSunTimes(now, lat, lng);
  if (isNaN(sunrise.getTime()) || isNaN(sunset.getTime())) return false;
  return now < sunrise || now >= sunset;
}
